import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Carrier } from '@app/services/carriers/carrier.model';
import { OrderBatchService } from '@app/services/order-batch/order-batch.service';
import { ShopService } from '@app/services/shops/shop.service';
import { UserService } from '@app/services/user/user.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { BatchesState, BatchFilters, BatchFilterOptions } from '@app/sharedModule/models/paginated-batch-table.model';
import {
  createLocationFilterOptions,
  createOwnerFilterOptions,
  createCarrierFilterOptions,
} from '@utils/batch-filter.utils';
import { ADD_TO_BATCH_TABLE_COLUMNS } from './add-to-batches-table.config';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { Order } from '@app/services/order/order.model';
import moment from 'moment-timezone';
import { OrderBatchCategory, OrderBatchDTO } from '@pdqdev/pdq-shared/dist/entities';
import { OrderBatchVirtualColumn } from '@pdqdev/pdq-shared/dist/interfaces';
import { finalize } from 'rxjs';

// Extended batch type with virtual columns from backend and mismatch error for UI
interface AddToBatchDTO extends OrderBatchDTO {
  isPickupBatch?: boolean;
  isInternationalBatch?: boolean;
  mismatchError?: string;
  addToBatchLoading?: boolean;
}

@Component({
  selector: 'add-to-batches-table',
  templateUrl: './add-to-batches-table.component.html',
  styleUrls: ['./add-to-batches-table.component.scss'],
  standalone: false,
})
export class AddToBatchesTableComponent implements OnInit, OnChanges {
  @Input() selectedOrders: Order[] = [];
  @Input() shop: Shop;
  @Input() carriers: Carrier[] = [];
  @Input() selectedBatchId: string | null = null;

  @Output() batchSelected = new EventEmitter<OrderBatchDTO>();
  @Output() loadingChange = new EventEmitter<boolean>();

  columns = [...ADD_TO_BATCH_TABLE_COLUMNS.map((col) => ({ ...col }))];
  countLoading = false;

  // Filter options
  locations = computed(() => this.createLocationOptions());
  owners: BatchFilterOptions;
  carrierOptions: BatchFilterOptions;

  // Pagination state
  private _batchesState = signal<BatchesState>({
    pageCache: new Map(),
    loadingPages: new Set(),
    pageSize: 5,
    filters: {},
  });
  private static readonly _BATCH_DTO_VIRTUAL_COLUMNS: OrderBatchVirtualColumn[] = [
    'hasAddedOrders',
    'isInternationalBatch',
    'fulfilledOrdersCount',
    'isPickupBatch',
  ];
  private _totalCount = signal<number>(0);
  private destroyRef = inject(DestroyRef);

  constructor(
    private orderBatchService: OrderBatchService,
    private shopService: ShopService,
    private userService: UserService,
    private toastService: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedBatchId) {
      this.setAddToBatchColumnSpecifics();
    }
    if (changes.carriers) {
      this.setCarriersOptions();
    }
  }

  ngOnInit(): void {
    this.setAddToBatchColumnSpecifics();
    this.setOwnerOptions();
    this.setCarriersOptions();
  }

  get batchesState() {
    return this._batchesState.asReadonly();
  }

  get totalCount() {
    return this._totalCount.asReadonly();
  }

  private setAddToBatchColumnSpecifics() {
    const addToBatchColumn = this.columns.find((col) => col.key === 'addtobatch');
    if (addToBatchColumn) {
      addToBatchColumn.columnSpecifics = {
        selectedBatchId: this.selectedBatchId,
      };
    }
    this.columns = [...this.columns.map((col) => ({ ...col }))];
  }

  private createLocationOptions(): BatchFilterOptions {
    const shopLocations = this.shopService.shopSignal()?.shopLocations || [];
    const userLocations = this.userService.pdqUserSignal()?.locations;
    return createLocationFilterOptions(shopLocations, userLocations);
  }

  private setOwnerOptions(): void {
    this.userService.users$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((users) => {
      if (!users?.length) {
        return;
      }
      this.owners = createOwnerFilterOptions(users);
    });
  }

  private setCarriersOptions(): void {
    this.carrierOptions = createCarrierFilterOptions(this.carriers);
  }

  // ============================================
  // PUBLIC API FOR CHILD COMPONENTS
  // ============================================

  initPageSize(pageSize: number): void {
    const currentState = this._batchesState();

    this._batchesState.set({
      ...currentState,
      pageSize,
      pageCache: new Map(),
      loadingPages: new Set(),
    });

    // Fetch count and first page
    this.fetchCount();
    this.loadPageToCache(1);
  }

  private fetchCount(): void {
    this.countLoading = true;
    const state = this._batchesState();
    this.loadingChange.emit(true);

    this.orderBatchService
      .getBatchesCount({ category: OrderBatchCategory.FULFILLMENT_READY, ...state.filters })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.countLoading = false)),
      )
      .subscribe({
        next: ({ count }) => {
          this._totalCount.set(count);
          this.loadingChange.emit(false);
        },
        error: () => {
          this.toastService.showToast({ message: 'Failed to load batch count', mode: 'danger' });
          this.loadingChange.emit(false);
        },
      });
  }

  loadPageToCache(page: number): void {
    const state = this._batchesState();

    // Skip if already cached or loading
    if (state.pageCache.has(page) || state.loadingPages.has(page)) {
      return;
    }

    // Mark as loading
    const newLoadingPages = new Set(state.loadingPages);
    newLoadingPages.add(page);
    this._batchesState.set({ ...state, loadingPages: newLoadingPages });

    this.orderBatchService
      .getBatchesPage({
        category: OrderBatchCategory.FULFILLMENT_READY,
        skip: (page - 1) * state.pageSize,
        take: state.pageSize,
        virtualColumns: AddToBatchesTableComponent._BATCH_DTO_VIRTUAL_COLUMNS,
        ...state.filters,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ batches }) => {
          const currentState = this._batchesState();
          const newCache = new Map(currentState.pageCache);
          // Add mismatch errors to batches
          const batchesWithErrors = batches.map((batch) => ({
            ...batch,
            mismatchError: this.checkBatchMismatchErrors(batch),
          }));
          newCache.set(page, batchesWithErrors);
          const newLoading = new Set(currentState.loadingPages);
          newLoading.delete(page);

          this._batchesState.set({
            ...currentState,
            pageCache: newCache,
            loadingPages: newLoading,
          });
        },
        error: () => {
          const currentState = this._batchesState();
          const newLoading = new Set(currentState.loadingPages);
          newLoading.delete(page);
          this._batchesState.set({ ...currentState, loadingPages: newLoading });
          this.toastService.showToast({ message: 'Failed to load batches', mode: 'danger' });
        },
      });
  }

  preloadAdjacentPages(currentPage: number): void {
    const state = this._batchesState();
    const totalCount = this._totalCount();
    const totalPages = Math.ceil(totalCount / state.pageSize);

    if (currentPage > 1) {
      this.loadPageToCache(currentPage - 1);
    }
    if (currentPage < totalPages) {
      this.loadPageToCache(currentPage + 1);
    }
  }

  onLoadPage(event: { page: number }): void {
    this.loadPageToCache(event.page);
  }

  setFilters(filters: BatchFilters): void {
    const currentState = this._batchesState();

    this._batchesState.set({
      ...currentState,
      pageCache: new Map(),
      loadingPages: new Set(),
      filters: {
        ...filters,
      },
    });

    // Fetch new count and first page
    this.fetchCount();
    this.loadPageToCache(1);
  }

  // ============================================
  // BATCH VALIDATION
  // ============================================

  private checkBatchMismatchErrors(batch: AddToBatchDTO): string {
    return [
      this.checkDifferentShipFromLocations(batch),
      this.checkMixPickupVsShipping(batch),
      this.checkInternationalVsDomestic(batch),
      this.checkPassedPickupDate(batch),
    ]
      .filter(Boolean)
      .join('\n');
  }

  private checkDifferentShipFromLocations(batch: AddToBatchDTO): string {
    const shipFromId = batch.shipFromAddress?.locationId;
    return this.selectedOrders.some((order) => order.shipFromAddress?.locationId !== shipFromId)
      ? "*Orders from different inventories can't be batched"
      : '';
  }

  private checkMixPickupVsShipping(batch: AddToBatchDTO): string {
    const isPickupBatch = batch.isPickupBatch ?? false;
    const hasPickupOrders = this.selectedOrders.some((order) => order.pickUpLocationId && !order.shippingAddress);
    const hasShippingOrders = this.selectedOrders.some((order) => !order.pickUpLocationId || order.shippingAddress);

    if (isPickupBatch && hasShippingOrders) {
      return "*Pickup & Carrier orders can't be batched together";
    }
    if (!isPickupBatch && hasPickupOrders) {
      return "*Pickup & Carrier orders can't be batched together";
    }
    return '';
  }

  private checkInternationalVsDomestic(batch: AddToBatchDTO): string {
    const isInternationalBatch = batch.isInternationalBatch ?? false;
    const shopCountry = this.shop?.address?.country ?? this.shop?.address?.countryCode;

    const hasInternationalOrders = this.selectedOrders.some((order) => {
      const shipFromCountry = order.shipFromAddress?.countryCode || order.shipFromAddress?.country || shopCountry;
      return (
        !!order.shippingAddress &&
        order.shippingAddress.countryCode !== shipFromCountry &&
        order.shippingAddress.country !== shipFromCountry
      );
    });

    const hasDomesticOrders = this.selectedOrders.some((order) => {
      const shipFromCountry = order.shipFromAddress?.countryCode || order.shipFromAddress?.country || shopCountry;
      return (
        !order.shippingAddress ||
        order.shippingAddress.countryCode === shipFromCountry ||
        order.shippingAddress.country === shipFromCountry
      );
    });

    if (isInternationalBatch && hasDomesticOrders) {
      return "*International & Domestic orders can't be batched together";
    }
    if (!isInternationalBatch && hasInternationalOrders) {
      return "*International & Domestic orders can't be batched together";
    }
    return '';
  }

  private checkPassedPickupDate(batch: AddToBatchDTO): string {
    if (!batch.pickupDate) return '';

    const shopTimezone = this.shop?.metadata?.ianaTimeZone ?? 'UTC';
    const todayInShopTimezone = moment.tz(new Date(), shopTimezone).startOf('day');
    const pickupDateInShopTimezone = moment.tz(batch.pickupDate, shopTimezone).startOf('day');

    return pickupDateInShopTimezone.isBefore(todayInShopTimezone)
      ? "*Can't add orders to batch with passed pickup date"
      : '';
  }

  // ============================================
  // UI ACTIONS
  // ============================================

  selectBatch(batch: AddToBatchDTO): void {
    batch.addToBatchLoading = true;
    this.batchSelected.emit(batch);
  }
}
